/**
 * EnterpriseModal — Shared modal wrapper for enterprise modules
 *
 * Provides: overlay blur, Esc to close, header/content/footer structure, dark mode
 */

import React, { useEffect, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';

export interface EnterpriseModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  loading?: boolean;
  /** Max width class, default 'max-w-lg' */
  maxWidth?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const EnterpriseModal: React.FC<EnterpriseModalProps> = React.memo(
  ({ open, onClose, title, subtitle, loading, maxWidth = 'max-w-lg', children, footer }) => {
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !loading) onClose();
      },
      [onClose, loading],
    );

    useEffect(() => {
      if (open) {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
      }
    }, [open, handleKeyDown]);

    if (!open) return null;

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={loading ? undefined : onClose}
        />
        <div className={`relative w-full ${maxWidth} mx-4 bg-white dark:bg-[var(--bg-surface,#1e293b)] rounded shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200`}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100 dark:border-slate-700">
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">{title}</h3>
              {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{subtitle}</p>}
            </div>
            {!loading && (
              <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors">
                <X size={14} className="text-gray-400" />
              </button>
            )}
            {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  },
);

/** Reusable form field wrapper */
export const ModalField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">{label}</label>
    {children}
  </div>
);

/** Shared input class */
export const modalInputClass =
  'w-full rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm text-gray-900 dark:text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition';

/** Shared select class */
export const modalSelectClass = modalInputClass;

/** Primary button */
export const ModalPrimaryButton: React.FC<{ onClick: () => void; disabled?: boolean; loading?: boolean; children: React.ReactNode }> = ({ onClick, disabled, loading, children }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className="px-4 py-1.5 text-xs font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-1.5"
  >
    {loading && <Loader2 size={12} className="animate-spin" />}
    {children}
  </button>
);

/** Secondary button */
export const ModalSecondaryButton: React.FC<{ onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({ onClick, disabled, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="px-4 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 transition"
  >
    {children}
  </button>
);
